import React, { useState } from 'react';
import { ZenLogo } from '../landing/LandingPage';
import supabase from '../supabaseClient';

const bg     = '#060d1a';
const bg1    = '#0d1829';
const bg2    = '#111f35';
const border = '#1a2d47';
const text   = '#eef2f7';
const muted  = '#ddeaf5';
const dim    = '#a0bdd0';
const accent = '#0ea5e9';
const accentD= '#0284c7';
const green  = '#10b981';
const font   = "'Inter', system-ui, -apple-system, sans-serif";

const TERMS_VERSION   = '2026-04-16';
const PRIVACY_VERSION = '2026-04-16';

const plans = [
  {
    id: 'garmin', // technical plan key (Stripe price), not shown to users
    name: 'ZenAnalytics',
    price: '10',
    trial: true,
    topTag: '14-day free — then €10/mo',
    tagline: 'AI performance analytics for racket sport players',
    description: 'Plan every match, log every result, and let Data Coach AI find the patterns in your own game.',
    features: [
      'Game Plan — prepare, focus and debrief every training or match',
      'Game Stats — match history, head-to-head records & notes',
      'Data Coach — an AI coach that reads your plans, results and notes',
    ],
    dataNote: 'Every plan, result and note stays in your match library',
  },
];

export default function PlanSelectPage({ user, onBack, onShowTerms, onShowPrivacy, onTryDemo }) {
  const selectedPlan = 'garmin';
  const [termsAccepted, setTermsAccepted]         = useState(false);
  const [loading, setLoading]                     = useState(false);
  const [error, setError]                         = useState(null);

  const isTrialPlan = true; // 14-day free trial
  const canProceed = termsAccepted;

  const handleProceed = async () => {
    if (!canProceed || loading) return;
    setLoading(true);
    setError(null);

    try {
      // upsert so re-trying checkout (e.g. after webhook delay) doesn't fail on duplicate key
      const { error: consentError } = await supabase
        .from('consents')
        .upsert({
          user_id:           user.id,
          terms_version:     TERMS_VERSION,
          privacy_version:   PRIVACY_VERSION,
          biometric_consent: false, // no biometric data is processed
          user_agent:        navigator.userAgent,
        }, { onConflict: 'user_id,terms_version' });

      if (consentError && consentError.code !== '23505') throw consentError; // ignore true duplicate

      // All plans go through Stripe — Insights gets 14-day trial via subscription_data
      const { data: { session: authSession } } = await supabase.auth.getSession();
      const res = await fetch(`${process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000'}/stripe/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authSession?.access_token && { Authorization: `Bearer ${authSession.access_token}` }),
        },
        body: JSON.stringify({
          user_id: user.id,
          email:   user.email,
          plan:    selectedPlan,
          success_url: window.location.origin + '/?checkout=success',
          cancel_url:  window.location.origin + '/?checkout=cancelled',
        }),
      });

      if (!res.ok) throw new Error('Failed to create checkout session');
      const { url } = await res.json();
      // Mark that checkout was initiated — used to handle webhook timing delay on return
      localStorage.setItem('zd_checkout_ts', String(Date.now()));
      window.location.href = url;

    } catch (err) {
      console.error(err);
      setError('Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  const BG_PLAN = 'https://images.unsplash.com/photo-1528543606781-2f6e6857f318?auto=format&fit=crop&w=1920&q=80';

  return (
    <div style={{ minHeight: '100vh', fontFamily: font, position: 'relative' }}>

      {/* Background image */}
      <div style={{
        position: 'fixed', inset: 0,
        backgroundImage: `linear-gradient(180deg, rgba(6,13,26,0.50) 0%, rgba(6,13,26,0.45) 50%, rgba(6,13,26,0.55) 100%), url(${BG_PLAN})`,
        backgroundSize: 'cover', backgroundPosition: 'center center',
        backgroundRepeat: 'no-repeat',
        filter: 'saturate(1.2) brightness(1.05)',
        zIndex: 0,
      }} />

      {/* Nav */}
      <nav style={{
        borderBottom: `1px solid ${border}`,
        display: 'flex', alignItems: 'center', gap: '16px',
        position: 'fixed', top: 0, left: 0, right: 0,
        background: 'rgba(6,13,26,0.85)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        zIndex: 10,
        height: '64px',
        padding: '0 24px',
      }}>
        <button onClick={onBack} style={{
          background: 'none', border: 'none', color: dim,
          fontSize: '14px', cursor: 'pointer', fontFamily: font,
          display: 'flex', alignItems: 'center', gap: '6px',
          transition: 'color 0.15s',
        }}
          onMouseEnter={e => e.currentTarget.style.color = text}
          onMouseLeave={e => e.currentTarget.style.color = dim}>
          ← Back
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <ZenLogo size={28} />
          <div>
            <span style={{ color: text, fontWeight: '800', fontSize: '16px', letterSpacing: '-0.4px', display: 'block', lineHeight: 1.1 }}>
              ZenAnalytics
            </span>
            <span style={{ color: dim, fontSize: '10px', letterSpacing: '0.05em', lineHeight: 1 }}>
              Train on Insight
            </span>
          </div>
        </div>

        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
          {['Register', 'Choose plan', 'Your profile'].map((step, i) => (
            <React.Fragment key={step}>
              <span style={{
                fontSize: '12px', fontWeight: i === 1 ? '700' : '500',
                color: i === 1 ? accent : dim,
              }}>
                {step}
              </span>
              {i < 2 && <span style={{ color: border, fontSize: '12px' }}>→</span>}
            </React.Fragment>
          ))}
        </div>
      </nav>

      <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '112px 20px 80px', position: 'relative', zIndex: 2 }}>

        {/* Heading */}
        <div style={{ textAlign: 'center', marginBottom: '48px' }}>
          <h1 style={{ color: text, fontSize: 'clamp(24px, 4vw, 36px)', fontWeight: '900', margin: '0 0 12px', letterSpacing: '-0.8px' }}>
            Start your free trial.
          </h1>
          <p style={{ color: muted, fontSize: '15px', margin: 0, maxWidth: '640px', marginLeft: 'auto', marginRight: 'auto' }}>
            One product, everything included. Price incl. VAT.
          </p>
        </div>

        {/* Plan card */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr',
          maxWidth: '460px',
          margin: '0 auto 32px',
        }}>
          {plans.map(plan => {
            return (
              <div
                key={plan.id}
                style={{
                  background: bg2,
                  border: `2px solid ${accent}`,
                  borderRadius: '18px',
                  position: 'relative',
                  boxShadow: `0 0 40px ${accent}25`,
                  display: 'flex', flexDirection: 'column',
                  overflow: 'hidden',
                }}>

                {/* Top tag */}
                {plan.topTag && (
                  <div style={{
                    padding: '10px 24px',
                    background: `${accent}22`,
                    borderBottom: `1px solid ${accent}25`,
                    color: accent,
                    fontSize: '11px', fontWeight: '600',
                    textAlign: 'center', letterSpacing: '0.02em',
                  }}>
                    {plan.topTag}
                  </div>
                )}

                <div style={{ padding: '28px 28px 24px', flex: 1, display: 'flex', flexDirection: 'column' }}>

                  <div style={{ color: accent, fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: '4px' }}>
                    {plan.name}
                  </div>
                  <div style={{ color: dim, fontSize: '11px', marginBottom: '12px' }}>
                    {plan.tagline}
                  </div>

                  <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px', marginBottom: '16px' }}>
                    <span style={{ color: '#ffffff', fontSize: '44px', fontWeight: '900', letterSpacing: '-2px' }}>€{plan.price}</span>
                    <span style={{ color: muted, fontSize: '14px' }}>/mo</span>
                  </div>

                  <p style={{ color: dim, fontSize: '12px', lineHeight: 1.6, margin: '0 0 20px' }}>
                    {plan.description}
                  </p>

                  <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 auto', display: 'flex', flexDirection: 'column', gap: '10px', flex: 1 }}>
                    {plan.features.map((f, j) => (
                      <li key={j} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                        <span style={{ color: green, fontSize: '13px', flexShrink: 0, marginTop: '2px' }}>✓</span>
                        <span style={{ color: '#ffffff', fontSize: '13px', lineHeight: 1.5 }}>{f}</span>
                      </li>
                    ))}
                  </ul>

                  {plan.dataNote && (
                    <p style={{ color: dim, fontSize: '11px', margin: '16px 0 0', fontStyle: 'italic', borderTop: `1px solid ${border}`, paddingTop: '12px' }}>
                      {plan.dataNote}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Consent checkboxes */}
        <div style={{
          background: bg1, border: `1px solid ${border}`,
          borderRadius: '14px', padding: '28px',
          maxWidth: '780px', margin: '0 auto 24px',
        }}>
          <h3 style={{ color: text, fontSize: '15px', fontWeight: '700', margin: '0 0 20px' }}>
            Before you continue
          </h3>

          <label style={{ display: 'flex', gap: '14px', alignItems: 'flex-start', cursor: 'pointer', marginBottom: '18px' }}>
            <input
              type="checkbox"
              checked={termsAccepted}
              onChange={e => setTermsAccepted(e.target.checked)}
              style={{ marginTop: '3px', width: '17px', height: '17px', accentColor: accent, flexShrink: 0, cursor: 'pointer' }}
            />
            <span style={{ color: muted, fontSize: '14px', lineHeight: 1.6 }}>
              I agree to the{' '}
              <button onClick={onShowTerms} style={{ background: 'none', border: 'none', color: accent, fontSize: '14px', fontWeight: '600', cursor: 'pointer', padding: 0, fontFamily: font, textDecoration: 'underline' }}>
                Terms of Service
              </button>
              {' '}and{' '}
              <button onClick={onShowPrivacy} style={{ background: 'none', border: 'none', color: accent, fontSize: '14px', fontWeight: '600', cursor: 'pointer', padding: 0, fontFamily: font, textDecoration: 'underline' }}>
                Privacy Policy
              </button>
            </span>
          </label>

        </div>

        {/* Error */}
        {error && (
          <div style={{ textAlign: 'center', color: '#f87171', fontSize: '14px', marginBottom: '16px' }}>
            {error}
          </div>
        )}

        {/* CTA */}
        <div style={{ textAlign: 'center', maxWidth: '780px', margin: '0 auto' }}>
          <button
            onClick={handleProceed}
            disabled={!canProceed || loading}
            style={{
              background: canProceed ? accent : border,
              color: canProceed ? '#fff' : dim,
              border: 'none', borderRadius: '12px',
              padding: '15px 36px', fontSize: '16px', fontWeight: '700',
              cursor: canProceed ? 'pointer' : 'not-allowed',
              fontFamily: font, letterSpacing: '-0.3px',
              transition: 'background 0.15s, transform 0.1s',
              width: '100%', maxWidth: '420px',
            }}
            onMouseEnter={e => { if (canProceed) { e.currentTarget.style.background = accentD; e.currentTarget.style.transform = 'translateY(-1px)'; } }}
            onMouseLeave={e => { if (canProceed) { e.currentTarget.style.background = accent; e.currentTarget.style.transform = 'translateY(0)'; } }}>
            {loading
              ? 'Preparing checkout...'
              : (isTrialPlan ? 'Start free trial →' : 'Proceed to checkout →')
            }
          </button>

          <p style={{ color: dim, fontSize: '12px', marginTop: '12px', lineHeight: 1.6 }}>
            No payment today. Your first payment is charged after the 14-day free trial ends.
          </p>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', marginTop: '8px' }}>
            <span style={{ fontSize: '13px' }}>🔒</span>
            <span style={{ color: dim, fontSize: '12px' }}>
              Payments handled by{' '}
              <a href="https://stripe.com" target="_blank" rel="noopener noreferrer"
                style={{ color: accent, fontWeight: '600', textDecoration: 'none' }}>
                Stripe
              </a>
              . We never see or store your card details.
            </span>
          </div>

          {!canProceed && (
            <p style={{ color: dim, fontSize: '12px', marginTop: '10px' }}>
              Please accept all checkboxes to continue.
            </p>
          )}


        </div>
      </div>
    </div>
  );
}
